import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import {
  Box, 
  Typography, 
  Card, 
  CardContent,
  CardHeader, 
  TableContainer, 
  Paper, 
  Grid, 
  Avatar, 
  Divider, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow,
  Chip,
  IconButton,
  Container,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AccountBalance as BankIcon,
  Security as SecurityIcon,
  Add as AddIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';

// Import static data
import { employeesData } from '../../Data/Kontak/Employee';
import { generateCandidatePrintHTML, generateEmployeeFormPrintHTML, openFormPrintPreview } from '../../utils/printUtils';
import { decodeToken } from '../../utils/auth';

const ViewEmployee = () => {
  const { id } = useParams();
  const [employee, setEmployee] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [gcsFiles, setGcsFiles] = useState([]);
  const [photoUrl, setPhotoUrl] = useState(null); 
  const navigate = useNavigate();
  
  // Add Document Modal State
  const [openAddDocModal, setOpenAddDocModal] = useState(false);
  const [docForm, setDocForm] = useState({
    document_name: '',
    document_code: '',
    id_document_category: '',
    user_upload: '',
    note: '',
    pic_name: '',
    start_date: '',
    end_date: '',
    isLifeTime: false,
    files: []
  });
  const [documentCategories, setDocumentCategories] = useState([]);
  const [userId, setUserId] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  // Get username and userId from token on component mount

  // Fungsi untuk download semua dokumen dan gabung jadi satu PDF
  const handleDownloadAllDocuments = async () => {
    if (!employee) {
      Swal.fire({
        icon: 'warning',
        title: 'Data karyawan tidak tersedia',
        text: 'Silakan refresh halaman'
      });
      return;
    }

    try {
      Swal.fire({
        title: 'Memproses...',
        text: 'Mengunduh dan menggabungkan dokumen PDF',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Try ngrok URL first, fallback to production API
      const urls = [
        // 'https://apidashboard.eurekagroup.id/documents/download-all-gcs-pdf'
        'https://apidashboard.eurekagroup.id/documents/download-all-gcs-pdf'
      ];

      let response = null;
      let lastError = null;

      for (const url of urls) {
        try {
          const res = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'ngrok-skip-browser-warning': 'true'
            },
            body: JSON.stringify({
              emp_id: employee.emp_id,
              full_name: employee.full_name
            })
          });

          if (res.ok) {
            response = res;
            break;
          }
        } catch (err) {
          lastError = err;
          console.warn(`Failed to fetch from ${url}:`, err);
        }
      }

      if (!response) {
        throw new Error(lastError?.message || 'Tidak dapat terhubung ke server');
      }

      // Cek apakah response adalah PDF
      const contentType = response.headers.get('Content-Type');
      const blob = await response.blob();

      // Cek apakah blob adalah HTML (error page)
      if (blob.type.includes('text/html')) {
        const text = await blob.text();
        if (text.includes('ngrok') || text.includes('<!DOCTYPE html>')) {
          throw new Error('Server mengembalikan halaman error (HTML). Pastikan ngrok tunnel aktif atau gunakan API production.');
        }
      }

      // Verifikasi bahwa blob adalah PDF yang valid
      const arrayBuffer = await blob.arrayBuffer();
      const uint8 = new Uint8Array(arrayBuffer);
      const magic = String.fromCharCode(...uint8.slice(0, 4));
      
      if (magic !== '%PDF') {
        throw new Error('File yang diunduh bukan PDF yang valid. Response mungkin berupa HTML error page.');
      }

      // Download file
      const fileName = `${employee.full_name}-${employee.emp_id}-All-Documents.pdf`;
      const url = URL.createObjectURL(new Blob([arrayBuffer], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: 'Berhasil!',
        text: 'Semua dokumen PDF telah digabung dan diunduh',
        timer: 2000
      });

    } catch (err) {
      console.error('Error downloading all documents:', err);
      Swal.fire({
        icon: 'error',
        title: 'Gagal download/gabung PDF',
        html: `<p>${err.message || 'Terjadi kesalahan saat mengunduh dokumen'}</p>
               <small>Pastikan backend endpoint /documents/download-all-gcs-pdf tersedia dan mengembalikan file PDF yang valid.</small>`
      });
    }
  };
  useEffect(() => {
    const getUsernameFromToken = () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          // Try to decode as JWT first
          let decoded;
          if (token.includes('.')) {
            // JWT format
            decoded = decodeToken(token);
          } else {
            // Direct JSON format
            decoded = JSON.parse(token);
          }
          
          if (decoded) {
            if (decoded.username) {
              setDocForm(prev => ({
                ...prev,
                user_upload: decoded.username
              }));
            }
            if (decoded.userId) {
              setUserId(decoded.userId);
            }
          }
        }
      } catch (error) {
        console.error('Error getting username from token:', error);
      }
    };

    getUsernameFromToken();
  }, []);

  // Fetch document categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch('https://apidashboard.eurekagroup.id/document-categories');
        if (response.ok) {
          const data = await response.json();
          setDocumentCategories(data);
        }
      } catch (error) {
        console.error('Error fetching document categories:', error);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        // Check if it's a static ID
        if (id.startsWith('static_')) {
          const staticId = parseInt(id.replace('static_', ''));
          const staticEmployee = employeesData.find(emp => emp.id === staticId);
          
          if (staticEmployee) {
            // Convert static data to match API format and add static data
            const convertedEmployee = {
              _id: id,
              emp_id: staticEmployee.personalData.employee_code,
              full_name: staticEmployee.personalData.employee_name,
              sex: staticEmployee.personalData.gender === 'L' ? 'Male' : 'Female',
              email: staticEmployee.contact.corporate_email,
              no_telp: staticEmployee.contact.mobile_phone,
              is_active: staticEmployee.personalData.active_status === 1 ? 'Active' : 'Inactive',
              birth_date: staticEmployee.personalData.birth_date,
              address: staticEmployee.address.street,
              // Add all static data
              staticData: {
                personalData: staticEmployee.personalData,
                workInfo: staticEmployee.workInfo,
                address: staticEmployee.address,
                benefit: staticEmployee.benefit,
                contact: staticEmployee.contact,
                tax: staticEmployee.tax,
                others: staticEmployee.others
              },
              isStatic: true
            };
            
            setEmployee(convertedEmployee);
            setPhotoUrl(staticEmployee.personalData.photo);
            return;
          }
        }

        // Fetch from API for dynamic data
        const response = await fetch(`https://apidashboard.eurekagroup.id/employee/get/${id}`);
        
        if (response.ok) {
          const data = await response.json();
          console.log('Employee data from API:', data);
          console.log('Education data:', data.education_data);
          // Add isStatic flag for dynamic data
          setEmployee({ ...data, isStatic: false });

          if (data.photo) {
            const photoResponse = await fetch(`https://apidashboard.eurekagroup.id/employee/view/${data.photo}`, {
              method: 'GET',
              mode: 'cors'
            });

            if (photoResponse.ok) {
              const blob = await photoResponse.blob();
              const imageUrl = URL.createObjectURL(blob);
              setPhotoUrl(imageUrl); 
            }
          }

          // Fetch files listed in GCS for this employee
          await fetchGcsList(data);
        } else {
          Swal.fire('Error', 'Failed to fetch employee details', 'error');
        }
      } catch (error) {
        console.error('Error fetching employee:', error);
        Swal.fire('Error', 'There was an error loading the employee data', 'error');
      }
    };

    fetchEmployee();
  }, [id]);

  // Fetch list of files from GCS for a given employee via backend POST endpoint
  const fetchGcsList = async (employeeObj) => {
    if (!employeeObj) {
      setGcsFiles([]);
      setDocuments([]);
      return [];
    }
    try {
      const resp = await fetch('https://apidashboard.eurekagroup.id/documents/list-gcs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ emp_id: employeeObj.emp_id || '', full_name: employeeObj.full_name || '' })
      });

      if (!resp.ok) {
        console.error('Failed to fetch GCS list:', resp.status);
        setGcsFiles([]);
        setDocuments([]);
        return [];
      }

      const json = await resp.json();

      // Handle the response structure: { success, folder, items: [...] }
      let items = [];
      if (json.success && Array.isArray(json.items)) {
        items = json.items;
      } else if (Array.isArray(json)) {
        items = json;
      }

      // Normalize returned items from GCS list response
      const normalized = items.map(item => {
        if (!item) return null;
        if (typeof item === 'string') {
          return { 
            filename: item,
            size: null, 
            contentType: null,
            created: null,
            updated: null,
            viewUrl: null, 
            downloadUrl: null, 
            gsUri: null
          };
        }
        if (typeof item === 'object') {
          return {
            filename: item.filename || item.name || '',
            size: item.size || null,
            contentType: item.contentType || item.content_type || null,
            created: item.created || item.timeCreated || null,
            updated: item.updated || item.timeUpdated || null,
            viewUrl: item.viewUrl || null,
            downloadUrl: item.downloadUrl || null,
            gsUri: item.gsUri || null,
            storageClass: item.storageClass || null,
            publicAccess: item.publicAccess || null
          };
        }
        return null;
      }).filter(Boolean);

      setGcsFiles(normalized);
      setDocuments(normalized); // Set documents from GCS list
      
      // Update employee document count
      setEmployee(prev => ({
        ...prev,
        document_count: normalized.length
      }));
      
      return normalized;
    } catch (error) {
      console.error('Error fetching GCS list:', error);
      setGcsFiles([]);
      setDocuments([]);
      return [];
    }
  };

  // Helpers to build GCS view/download URLs
  const buildGcsViewUrl = (employee, documentName) => {
    if (!employee) return '#';
    const nama = encodeURIComponent(employee.full_name || '');
    const nim = encodeURIComponent(employee.emp_id || '');
    const sanitizedLabel = (documentName || '')
      .replace(/\([^)]*\)/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    const document_type = encodeURIComponent(sanitizedLabel);
    return `https://apidashboard.eurekagroup.id/documents/view-gcs?nama=${nama}&nim=${nim}&document_type=${document_type}`;
  };

  const buildGcsDownloadUrl = (employee, documentName) => {
    if (!employee) return '#';
    const nama = encodeURIComponent(employee.full_name || '');
    const nim = encodeURIComponent(employee.emp_id || '');
    const sanitizedLabel = (documentName || '')
      .replace(/\([^)]*\)/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    const document_type = encodeURIComponent(sanitizedLabel);
    return `https://apidashboard.eurekagroup.id/documents/download-gcs?nama=${nama}&nim=${nim}&document_type=${document_type}`;
  };

  const handleDocumentClick = (docId) => {
    navigate(`/document/ViewDocument/${docId}`);
  };

  const handleFormPrint = () => {
    if (employee) {
      // Debug: log the employee object passed to the print util
      try { console.debug('ViewEmployee.handleFormPrint - employee:', employee); } catch (e) {}

  // Use the Employee form-style print template which reads employee fields directly
  const printHTML = generateEmployeeFormPrintHTML(employee);

      // Create Blob from HTML string
      const blob = new Blob([printHTML], { type: 'text/html' });
      const blobURL = URL.createObjectURL(blob);

      // Open in new window with blob URL
      const printWindow = window.open(blobURL, '_blank');

      // Trigger print dialog after content loads
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      }
    }
  };

  // Handle Add Document Modal
  const handleOpenAddDocModal = () => {
    // Ensure username is set from token when opening modal
    try {
      const token = localStorage.getItem('token');
      if (token) {
        let decoded;
        if (token.includes('.')) {
          // JWT format
          decoded = decodeToken(token);
        } else {
          // Direct JSON format
          decoded = JSON.parse(token);
        }
        
        if (decoded && decoded.username) {
          setDocForm(prev => ({
            ...prev,
            user_upload: decoded.username
          }));
        }
      }
    } catch (error) {
      console.error('Error getting username from token:', error);
    }
    setOpenAddDocModal(true);
  };

  const handleCloseAddDocModal = () => {
    setOpenAddDocModal(false);
    // Get username from token to preserve it
    let username = '';
    try {
      const token = localStorage.getItem('token');
      if (token) {
        let decoded;
        if (token.includes('.')) {
          decoded = decodeToken(token);
        } else {
          decoded = JSON.parse(token);
        }
        if (decoded && decoded.username) {
          username = decoded.username;
        }
      }
    } catch (error) {
      console.error('Error getting username from token:', error);
    }
    // Reset form but preserve username
    setDocForm({
      document_name: '',
      document_code: '',
      id_document_category: '',
      user_upload: username,
      note: '',
      pic_name: '',
      start_date: '',
      end_date: '',
      isLifeTime: false,
      files: []
    });
  };

  const handleDocFormChange = (field, value) => {
    // Prevent changing user_upload field
    if (field === 'user_upload') {
      return;
    }
    setDocForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    setDocForm(prev => ({
      ...prev,
      isLifeTime: e.target.checked,
      end_date: e.target.checked ? '' : prev.end_date,
    }));
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    setDocForm(prev => ({
      ...prev,
      files: [...prev.files, ...files]
    }));
  };

  const handleRemoveFile = (index) => {
    setDocForm(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const handleSubmitDocument = async () => {
    // Validation
    if (!docForm.document_name || !docForm.document_code || !docForm.id_document_category) {
      Swal.fire('Error', 'Mohon isi semua field yang wajib diisi (*)', 'error');
      return;
    }

    if (!employee || !employee._id) {
      Swal.fire('Error', 'Employee data tidak ditemukan', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      formData.append('document_name', docForm.document_name);
      formData.append('document_code', docForm.document_code);
      formData.append('id_document_category', docForm.id_document_category);
      formData.append('id_contact', employee._id);
      formData.append('modelType', 'employee'); 
      formData.append('id_office', employee.id_office?._id || '');
      formData.append('id_cabinet', '');
      formData.append('date_added', new Date().toISOString().split('T')[0]);
      formData.append('date_modified', new Date().toISOString().split('T')[0]);
      formData.append('id_user', userId || '');
      formData.append('note', docForm.note || '');
      formData.append('pic_name', docForm.pic_name || '');
      formData.append('start_date', docForm.start_date || '');
      
      if (!docForm.isLifeTime) {
        formData.append('end_date', docForm.end_date || '');
      }
      
      // Append file if available (take first file)
      if (docForm.files && docForm.files.length > 0) {
        formData.append('file_name', docForm.files[0]);
      }

      const response = await fetch('https://apidashboard.eurekagroup.id/documents/upload', {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const responseBody = await response.json();

      if (!response.ok) {
        throw new Error(responseBody.message || `Failed to save document. Status: ${response.status}`);
      }

      // Refresh documents list from GCS
      await fetchGcsList(employee);

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Document has been added successfully.',
        timer: 2000,
        showConfirmButton: false,
      });

      handleCloseAddDocModal();
    } catch (error) {
      console.error('Error adding document:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `There was an error saving the document: ${error.message}`
      });
    }
  };

  if (!employee) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh',
        gap: 3
      }}>
        <Box sx={{ position: 'relative' }}>
          <CircularProgress 
            size={60} 
            thickness={4}
            sx={{ 
              color: 'primary.main',
              animationDuration: '550ms'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <PersonIcon sx={{ color: 'primary.main', fontSize: 24 }} />
          </Box>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
            Memuat Data Karyawan
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Mohon tunggu sebentar...
          </Typography>
        </Box>
      </Box>
    );
  }

  const statusColor = employee.is_active === 'Active' ? 'green' : 'red';
  const statusText = employee.is_active === 'Active' ? 'Active' : 'Inactive';
  const genderIcon = employee.sex === 'Male' 
  ? '♂️' 
  : employee.sex === 'Female' 
    ? '♀️' 
    : '❓';

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Compact Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton 
            onClick={() => navigate('/kontak/employee')}
            size="small"
            sx={{ 
              bgcolor: 'grey.100',
              '&:hover': { bgcolor: 'grey.200' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Detail Karyawan
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PrintIcon />}
            onClick={handleFormPrint}
            sx={{ 
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 2
            }}
          >
            Print
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/kontak/employee/EditEmployee/${employee._id}`)}
            sx={{ 
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 2
            }}
          >
            Edit
          </Button>
        </Box>
      </Box>

      {/* Profile Summary Card */}
      <Card sx={{ mb: 2, boxShadow: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs="auto">
                <Avatar
                  src={photoUrl}
                  alt={employee.full_name}
                sx={{ width: 80, height: 80, border: '3px solid', borderColor: 'primary.light' }}
                >
                  {!photoUrl && <PersonIcon sx={{ fontSize: 40 }} />}
                </Avatar>
            </Grid>
            <Grid item xs>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {employee.full_name}
                </Typography>
                <Chip 
                  label={statusText} 
                  size="small"
                  color={employee.is_active === 'Active' ? 'success' : 'error'}
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
                {employee.isStatic && (
                  <Chip 
                    label="Static" 
                    size="small"
                    color="secondary"
                    sx={{ height: 22, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                ID: {employee.emp_id} • {genderIcon} {employee.sex} • {employee.birth_date ? new Date(employee.birth_date).toLocaleDateString('id-ID') : '-'}
                  </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption">{employee.no_telp || '-'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption">{employee.email || '-'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption">{employee.address || '-'}</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
            </CardContent>
          </Card>

      {/* Tabs Navigation */}
      <Card sx={{ mb: 2, boxShadow: 2, borderRadius: 2 }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
                  '& .MuiTab-root': { 
                    minHeight: 48,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }
                }}
              >
          <Tab label="Data Pribadi" icon={<PersonIcon />} iconPosition="start" />
          <Tab label="Pekerjaan" icon={<WorkIcon />} iconPosition="start" />
          <Tab label="Pendidikan" icon={<SchoolIcon />} iconPosition="start" />
          <Tab label="Kemampuan" icon={<SecurityIcon />} iconPosition="start" />
          <Tab label="Kesehatan" icon={<CalendarIcon />} iconPosition="start" />
          <Tab label="Kursus" icon={<SchoolIcon />} iconPosition="start" />
          <Tab label="Relasi" icon={<PersonIcon />} iconPosition="start" />
          <Tab label="Keluarga" icon={<PersonIcon />} iconPosition="start" />
          <Tab label="Dokumen" icon={<FileIcon />} iconPosition="start" />
              </Tabs>
      </Card>

      {/* Tab Content */}
      <Box sx={{ width: '100%' }}>
        {/* 0 - Data Pribadi */}
        {activeTab === 0 && (
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={2}>
                {[
                  { label: 'Nama Lengkap', value: employee.full_name || employee.staticData?.personalData?.full_name || '-', icon: <PersonIcon />, important: true },
                  { label: 'Nama Depan', value: employee.first_name || employee.staticData?.personalData?.first_name || '-', icon: <PersonIcon /> },
                  { label: 'Nama Tengah', value: employee.middle_name || employee.staticData?.personalData?.middle_name || '-', icon: <PersonIcon /> },
                  { label: 'Nama Belakang', value: employee.last_name || employee.staticData?.personalData?.last_name || '-', icon: <PersonIcon /> },
                  { label: 'Jenis Kelamin', value: employee.sex || (employee.staticData?.personalData?.gender === 'L' ? 'Male' : 'Female') || '-', icon: <PersonIcon /> },
                  { label: 'Tempat Lahir', value: employee.birth_place || employee.staticData?.personalData?.birth_place || '-', icon: <LocationIcon /> },
                  { label: 'Tanggal Lahir', value: employee.birth_date ? new Date(employee.birth_date).toLocaleDateString('en-GB') : (employee.staticData?.personalData?.birth_date ? new Date(employee.staticData.personalData.birth_date).toLocaleDateString('en-GB') : '-'), icon: <CalendarIcon /> },
                  { label: 'Agama', value: employee.religion || employee.staticData?.personalData?.religion || '-', icon: <PersonIcon /> },
                  { label: 'Status Pernikahan', value: employee.marital_status || employee.staticData?.personalData?.marital_status || '-', icon: <PersonIcon /> },
                  { label: 'Tanggal Menikah', value: employee.marital_date ? new Date(employee.marital_date).toLocaleDateString('en-GB') : (employee.staticData?.personalData?.marital_date ? new Date(employee.staticData.personalData.marital_date).toLocaleDateString('en-GB') : '-'), icon: <CalendarIcon /> },
                  { label: 'Nama Ibu Kandung', value: employee.mother_maiden_name || employee.staticData?.personalData?.mother_maiden_name || '-', icon: <PersonIcon /> },
                  { label: 'Suku', value: employee.ethnicity || employee.staticData?.personalData?.ethnicity || '-', icon: <PersonIcon /> },
                  { label: 'Usia', value: employee.age || employee.staticData?.personalData?.age || '-', icon: <PersonIcon /> },
                  { label: 'Tinggi (cm)', value: employee.height_cm || employee.staticData?.personalData?.height_cm || '-', icon: <PersonIcon /> },
                  { label: 'Berat (kg)', value: employee.weight_kg || employee.staticData?.personalData?.weight_kg || '-', icon: <PersonIcon /> },
                  { label: 'Alamat', value: employee.address || employee.staticData?.address?.street || '-', icon: <LocationIcon /> },
                  { label: 'Telepon', value: employee.no_telp || employee.staticData?.personalData?.phone || '-', icon: <PhoneIcon />, important: true },
                  { label: 'Email', value: employee.email || employee.staticData?.contact?.corporate_email || '-', icon: <EmailIcon />, important: true },
                  { label: 'BPJS TK', value: employee.bpjs_tk || employee.staticData?.benefit?.lbpjs_ketenagakerjaan_no || '-', icon: <BusinessIcon /> },
                  { label: 'BPJS Kesehatan', value: employee.bpjs_kes || employee.staticData?.benefit?.lbpjs_kesehatan_no || '-', icon: <BusinessIcon /> },
                  { label: 'Kategori Pajak', value: employee.tax_category || employee.staticData?.tax?.tax_category || '-', icon: <BusinessIcon /> },
                  { label: 'PPH21', value: employee.pph21 || employee.staticData?.tax?.pph21 || '-', icon: <BusinessIcon /> },
                  { label: 'Score', value: employee.highschool_avg_score || employee.staticData?.personalData?.highschool_avg_score || '-', icon: <SchoolIcon /> },
                  { label: 'Memiliki SIM', value: (employee.has_sim || employee.staticData?.personalData?.has_sim) ? 'Ya' : 'Tidak', icon: <WorkIcon /> },
                ].map(({ label, value, icon, important }, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                    <Box sx={{ p: 1.5, borderRadius: 1.5, background: 'rgba(158, 158, 158, 0.05)', border: '1px solid rgba(158, 158, 158, 0.1)', height: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        {React.cloneElement(icon, { sx: { color: 'grey.600', fontSize: 16, mr: 0.5 } })}
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700' }}>{label}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: important ? 'primary.main' : 'text.primary', fontWeight: important ? 600 : 400, fontSize: '0.875rem' }}>{value || '-'}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* SIM Detail */}
              {((employee.has_sim || employee.staticData?.personalData?.has_sim) && ((employee.sim_c_number || employee.staticData?.personalData?.sim_c_number) || (employee.sim_a_number || employee.staticData?.personalData?.sim_a_number) || (employee.sim_b_number || employee.staticData?.personalData?.sim_b_number))) && (
                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Surat Izin Mengemudi (SIM)</Typography>
                  <Grid container spacing={1.5}>
                    {(employee.sim_c_number || employee.staticData?.personalData?.sim_c_number) && (
                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 1.5, border: '1px solid', borderColor: 'info.300', bgcolor: 'info.50' }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'info.main', display: 'block', mb: 1 }}>SIM C</Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>No: {employee.sim_c_number || employee.staticData?.personalData?.sim_c_number || '-'}</Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>Valid: {employee.sim_c_valid_until || employee.staticData?.personalData?.sim_c_valid_until || '-'}</Typography>
                        </Paper>
                      </Grid>
                    )}
                    {(employee.sim_a_number || employee.staticData?.personalData?.sim_a_number) && (
                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 1.5, border: '1px solid', borderColor: 'success.300', bgcolor: 'success.50' }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.main', display: 'block', mb: 1 }}>SIM A</Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>No: {employee.sim_a_number || employee.staticData?.personalData?.sim_a_number || '-'}</Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>Valid: {employee.sim_a_valid_until || employee.staticData?.personalData?.sim_a_valid_until || '-'}</Typography>
                        </Paper>
                      </Grid>
                    )}
                    {(employee.sim_b_number || employee.staticData?.personalData?.sim_b_number) && (
                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 1.5, border: '1px solid', borderColor: 'warning.300', bgcolor: 'warning.50' }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.main', display: 'block', mb: 1 }}>SIM B</Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>No: {employee.sim_b_number || employee.staticData?.personalData?.sim_b_number || '-'}</Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>Valid: {employee.sim_b_valid_until || employee.staticData?.personalData?.sim_b_valid_until || '-'}</Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* 1 - Pekerjaan */}
        {activeTab === 1 && (
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={2}>
                {[
                  { label: 'Kantor', value: employee.id_office?.office_name || employee.staticData?.address?.location_id || '-', icon: <BusinessIcon /> },
                  { label: 'Level Pekerjaan', value: employee.id_job_level?.joblevel_name || '-', icon: <WorkIcon /> },
                  { label: 'Departemen', value: employee.id_department?.department_name || employee.staticData?.workInfo?.department || '-', icon: <BusinessIcon />, important: true },
                  { label: 'Designasi', value: employee.id_designation?.designation_name || '-', icon: <WorkIcon /> },
                  { label: 'Unit Bisnis', value: employee.id_bu?.bu_name || employee.staticData?.workInfo?.business_unit || '-', icon: <BusinessIcon />, important: true },
                  { label: 'Grade', value: employee.grade || employee.staticData?.workInfo?.grade || '-', icon: <WorkIcon /> },
                ].filter(item => item.value && item.value !== '-').map(({ label, value, icon, important }, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Box sx={{ p: 1.5, borderRadius: 1.5, background: 'rgba(158, 158, 158, 0.05)', border: '1px solid rgba(158, 158, 158, 0.1)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        {React.cloneElement(icon, { sx: { color: 'grey.600', fontSize: 16, mr: 0.5 } })}
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700' }}>{label}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: important ? 'primary.main' : 'text.primary', fontWeight: important ? 600 : 400, fontSize: '0.875rem' }}>{value || '-'}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Work History Table */}
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Riwayat Pekerjaan</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Masuk</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Keluar</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Posisi</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Perusahaan</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Keterangan</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {((employee.work_history_data && employee.work_history_data.length > 0) || (employee.staticData?.work_history && employee.staticData.work_history.length > 0)) ? (
                        (employee.work_history_data || employee.staticData?.work_history || []).map((work, index) => (
                          <TableRow key={index} hover>
                            <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{work.entry_date ? new Date(work.entry_date).toLocaleDateString('id-ID') : '-'}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{work.exit_date ? new Date(work.exit_date).toLocaleDateString('id-ID') : '-'}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{work.position || '-'}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{work.company || '-'}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{work.notes || '-'}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 2 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Tidak ada data riwayat pekerjaan</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* 2 - Pendidikan */}
        {activeTab === 2 && (
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Pendidikan Formal</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Jenjang</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Nama Sekolah</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Tempat</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Tahun Lulus</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Nilai/IPK</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Jurusan</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {((employee.education_data && employee.education_data.length > 0) || (employee.staticData?.education && employee.staticData.education.length > 0)) ? (
                      (employee.education_data || employee.staticData?.education || []).map((edu, index) => (
                        <TableRow key={index} hover>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{edu.level || edu.jenjang || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{edu.school_name || edu.nama_sekolah || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{edu.location || edu.tempat || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{edu.graduation_year || edu.tahun_lulus || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{edu.gpa || edu.rata_rata_nilai || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{edu.major || edu.jurusan || '-'}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Tidak ada data pendidikan formal</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* 3 - Kemampuan */}
        {activeTab === 3 && (
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Kemampuan</Typography>
              <Grid container spacing={2}>
                {[
                  { label: 'Skills', value: employee.skills || '-' },
                  { label: 'Computer Programs', value: employee.computer_programs || '-' },
                  { label: 'Driving Ability', value: employee.driving_ability || '-' },
                  { label: 'Reaction to Criticism', value: employee.reaction_to_criticism || '-' },
                  { label: 'Strength', value: employee.strength || '-' },
                  { label: 'Weakness', value: employee.weakness || '-' },
                  { label: 'Hobby', value: employee.hobby || '-' },
                  { label: 'Sport', value: employee.sport || '-' },
                  { label: 'Desired Position', value: employee.desired_position || '-' },
                  { label: 'Expected Salary', value: employee.expected_salary || '-' },
                ].map((item, idx) => (
                  <Grid item xs={12} sm={6} md={4} key={idx}>
                    <Box sx={{ p: 1.5, borderRadius: 1.5, background: 'rgba(158, 158, 158, 0.05)', border: '1px solid rgba(158, 158, 158, 0.1)' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>{item.label}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>{item.value}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* 4 - Kesehatan */}
        {activeTab === 4 && (
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Kesehatan</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 1.5, borderRadius: 1.5, background: 'rgba(158, 158, 158, 0.05)', border: '1px solid rgba(158, 158, 158, 0.1)' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>Tinggi (cm)</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>{employee.height_cm || '-'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 1.5, borderRadius: 1.5, background: 'rgba(158, 158, 158, 0.05)', border: '1px solid rgba(158, 158, 158, 0.1)' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>Berat (kg)</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>{employee.weight_kg || '-'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 1.5, borderRadius: 1.5, background: 'rgba(158, 158, 158, 0.05)', border: '1px solid rgba(158, 158, 158, 0.1)' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>Penyakit Umum</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>{employee.common_illness || '-'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 1.5, borderRadius: 1.5, background: 'rgba(158, 158, 158, 0.05)', border: '1px solid rgba(158, 158, 158, 0.1)' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>Terakhir Cek</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>{employee.last_checkup ? new Date(employee.last_checkup).toLocaleDateString('id-ID') : '-'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 1.5, borderRadius: 1.5, background: 'rgba(158, 158, 158, 0.05)', border: '1px solid rgba(158, 158, 158, 0.1)' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>BPJS Kesehatan</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>{employee.bpjs_kes || '-'}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* 5 - Kursus */}
        {activeTab === 5 && (
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Kursus/Pelatihan</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Instruktur/Institusi</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Lokasi</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Durasi</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Keterangan</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {((employee.courses_data && employee.courses_data.length > 0) || (employee.staticData?.courses && employee.staticData.courses.length > 0)) ? (
                      (employee.courses_data || employee.staticData?.courses || []).map((course, index) => (
                        <TableRow key={index} hover>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{course.instructor_institution || course.instruktur_lembaga || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{course.location || course.tempat || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{course.duration || course.lama_pendidikan || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{course.notes || course.keterangan || '-'}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Tidak ada data kursus/pelatihan</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* 6 - Relasi */}
        {activeTab === 6 && (
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Relasi Darurat</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 220 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Nama</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Usia</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Alamat</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Telepon</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Hubungan</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(employee.emergency_relations_data || []).map((r, i) => (
                      <TableRow key={i} hover>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{r.name || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{r.age || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{r.address || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{r.phone || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{r.relationship || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Relasi Lainnya</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Nama</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Usia</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Alamat</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Telepon</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Hubungan</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(employee.former_relations_data || []).map((r, i) => (
                      <TableRow key={i} hover>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{r.name || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{r.age || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{r.address || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{r.phone || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{r.relationship || '-'}</TableCell>
                      </TableRow>
                    ))}
                    {(employee.guarantors_data || []).map((r, i) => (
                      <TableRow key={`gu-${i}`} hover>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{r.name || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{r.age || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{r.address || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{r.phone || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{r.relationship || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* 7 - Keluarga */}
        {activeTab === 7 && (
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 1.5, borderRadius: 1.5, background: 'rgba(158, 158, 158, 0.05)', border: '1px solid rgba(158, 158, 158, 0.1)' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>Status Pernikahan</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>{employee.marital_status || employee.staticData?.personalData?.marital_status || '-'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 1.5, borderRadius: 1.5, background: 'rgba(158, 158, 158, 0.05)', border: '1px solid rgba(158, 158, 158, 0.1)' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>Tanggal Menikah</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>{employee.marital_date ? new Date(employee.marital_date).toLocaleDateString('en-GB') : (employee.staticData?.personalData?.marital_date ? new Date(employee.staticData.personalData.marital_date).toLocaleDateString('en-GB') : '-')}</Typography>
                  </Box>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Data Keluarga</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Jenis</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Nama</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>L/P</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Usia</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Pendidikan</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Pekerjaan</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Perusahaan</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(employee.family_data || []).length > 0 ? (
                      (employee.family_data || []).map((member, index) => (
                        <TableRow key={index} hover>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{member.type || member.jenis || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{member.name || member.nama || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{member.gender || member.gender || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{member.age || member.usia || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{member.education || member.pendidikan || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{member.job || member.pekerjaan || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{member.company || member.perusahaan || '-'}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Tidak ada data keluarga</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* 8 - Dokumen */}
        {activeTab === 8 && (
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Dokumen</Typography>
                    <Chip label={documents.length || 0} size="small" color="primary" sx={{ fontWeight: 'bold', height: 22 }} />
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<FileIcon />}
                    sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, px: 2 }}
                    onClick={handleDownloadAllDocuments}
                    disabled={documents.length === 0}
                  >
                    Download All Documents
                  </Button>
                </Box>
              }
              sx={{ bgcolor: 'grey.100', py: 1.5 }}
            />
            <CardContent sx={{ p: 2 }}>
              {documents.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <FileIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Tidak ada dokumen ditemukan
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {documents.map((doc, idx) => {
                    const filename = doc.filename || '-';
                    const fileSize = doc.size ? `${(doc.size / 1024).toFixed(2)} KB` : '-';
                    const uploadDate = doc.created ? new Date(doc.created).toLocaleDateString('id-ID', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : '-';
                    const viewUrl = doc.viewUrl || null;
                    const downloadUrl = doc.downloadUrl || null;
                    const contentType = doc.contentType || '';
                    
                    // Determine file icon based on content type
                    const isPdf = contentType.includes('pdf');
                    const isImage = contentType.includes('image');

                    return (
                      <Grid item xs={12} sm={6} md={4} key={idx}>
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            transition: 'all 0.2s',
                            '&:hover': {
                              boxShadow: 3,
                              transform: 'translateY(-2px)'
                            }
                          }}
                        >
                          <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                              <Box sx={{ 
                                p: 1, 
                                borderRadius: 1.5, 
                                bgcolor: isPdf ? 'error.50' : isImage ? 'success.50' : 'grey.100',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <FileIcon sx={{ 
                                  fontSize: 28, 
                                  color: isPdf ? 'error.main' : isImage ? 'success.main' : 'grey.600' 
                                }} />
                              </Box>
                              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: 600, 
                                    mb: 0.5,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    wordBreak: 'break-word'
                                  }}
                                  title={filename}
                                >
                                  {filename}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                  {fileSize}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.65rem' }}>
                                  {uploadDate}
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                          <Box sx={{ p: 1.5, pt: 0, display: 'flex', gap: 1 }}>
                            <Button
                              variant="contained"
                              size="small"
                              fullWidth
                              onClick={() => {
                                if (viewUrl) {
                                  window.open(viewUrl, '_blank', 'noopener,noreferrer');
                                } else {
                                  Swal.fire({
                                    icon: 'warning',
                                    title: 'View URL tidak tersedia',
                                    text: 'Dokumen tidak memiliki URL untuk ditampilkan'
                                  });
                                }
                              }}
                              sx={{ 
                                borderRadius: 1.5, 
                                fontSize: '0.7rem', 
                                py: 0.75,
                                textTransform: 'none',
                                fontWeight: 600
                              }}
                              disabled={!viewUrl}
                            >
                              View
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              fullWidth
                              onClick={() => {
                                if (downloadUrl) {
                                  window.open(downloadUrl, '_blank', 'noopener,noreferrer');
                                } else {
                                  Swal.fire({
                                    icon: 'warning',
                                    title: 'Download URL tidak tersedia',
                                    text: 'Dokumen tidak memiliki URL untuk diunduh'
                                  });
                                }
                              }}
                              sx={{ 
                                borderRadius: 1.5, 
                                fontSize: '0.7rem', 
                                py: 0.75,
                                textTransform: 'none',
                                fontWeight: 600
                              }}
                              disabled={!downloadUrl}
                            >
                              Download
                            </Button>
                          </Box>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Add Document Modal */}
      <Dialog 
        open={openAddDocModal} 
        onClose={handleCloseAddDocModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Add Document
          </Typography>
          <IconButton onClick={handleCloseAddDocModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Document Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Document Name"
                value={docForm.document_name}
                onChange={(e) => handleDocFormChange('document_name', e.target.value)}
                placeholder="Enter document name"
              />
            </Grid>

            {/* Document Code */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Document Code"
                value={docForm.document_code}
                onChange={(e) => handleDocFormChange('document_code', e.target.value)}
                placeholder="Enter document code"
              />
            </Grid>

            {/* Document Category */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Document Category</InputLabel>
                <Select
                  value={docForm.id_document_category}
                  label="Document Category"
                  onChange={(e) => handleDocFormChange('id_document_category', e.target.value)}
                >
                  {documentCategories.map((category) => (
                    <MenuItem key={category._id} value={category._id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Contact (Auto-filled from employee) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact"
                value={employee ? `${employee.full_name} (${employee.emp_id})` : ''}
                placeholder="Contact"
                InputProps={{
                  readOnly: true
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    cursor: 'default',
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover'
                  }
                }}
              />
            </Grid>

            {/* User Upload */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="User (Yang Upload)"
                value={docForm.user_upload}
                onChange={(e) => handleDocFormChange('user_upload', e.target.value)}
                placeholder="Enter user name"
                InputProps={{
                  readOnly: true
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    cursor: 'default',
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover'
                  }
                }}
              />
            </Grid>

            {/* PIC Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PIC Name"
                value={docForm.pic_name}
                onChange={(e) => handleDocFormChange('pic_name', e.target.value)}
                placeholder="Enter PIC name"
              />
            </Grid>

            {/* Start Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={docForm.start_date}
                onChange={(e) => handleDocFormChange('start_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* End Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={docForm.isLifeTime ? '' : docForm.end_date}
                onChange={(e) => handleDocFormChange('end_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={docForm.isLifeTime}
              />
            </Grid>

            {/* Seumur Hidup Checkbox */}
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={docForm.isLifeTime}
                    onChange={handleCheckboxChange}
                    color="primary"
                  />
                }
                label="Seumur Hidup"
              />
            </Grid>

            {/* Note */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Note"
                value={docForm.note}
                onChange={(e) => handleDocFormChange('note', e.target.value)}
                placeholder="Enter note or description"
              />
            </Grid>

            {/* File Upload */}
            <Grid item xs={12}>
              <Box sx={{ 
                border: '2px dashed',
                borderColor: 'grey.300',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover'
              }}>
                <input
                  accept="application/pdf,image/*"
                  style={{ display: 'none' }}
                  id="document-file-upload"
                  multiple
                  type="file"
                  onChange={handleFileUpload}
                />
                <label htmlFor="document-file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    sx={{ mb: 1 }}
                  >
                    Upload Files (PDF/Images)
                  </Button>
                </label>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                  Dapat upload multiple files (PDF atau gambar)
                </Typography>

                {/* Display uploaded files */}
                {docForm.files.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, textAlign: 'left' }}>
                      Files yang diupload:
                    </Typography>
                    <Stack spacing={1}>
                      {docForm.files.map((file, index) => (
                        <Box 
                          key={index}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            p: 1,
                            bgcolor: (theme) => theme.palette.background.paper,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.300'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FileIcon sx={{ color: 'primary.main' }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {file.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {(file.size / 1024).toFixed(2)} KB
                              </Typography>
                            </Box>
                          </Box>
                          <IconButton 
                            size="small" 
                            onClick={() => handleRemoveFile(index)}
                            sx={{ color: 'error.main' }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={handleCloseAddDocModal}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitDocument}
            variant="contained"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ViewEmployee;
